// Captura de atribuição (gclid + UTMs) no primeiro acesso — modelo first-touch.
// O gclid/UTMs precisam sobreviver do clique no anúncio até o cadastro (e daí
// ao banco, via profiles), permitindo importar a assinatura paga como conversão
// offline no Google Ads, mesmo quando a compra acontece dias depois do clique.

const STORAGE_KEY = 'meugantt_attribution';
const WINDOW_DAYS = 90;
const WINDOW_MS = WINDOW_DAYS * 24 * 60 * 60 * 1000;
const MAX_LEN = 512; // guarda contra URLs abusivas

export interface Attribution {
  gclid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

interface StoredAttribution extends Attribution {
  captured_at?: string; // ISO 8601
}

const PARAMS: (keyof Attribution)[] = [
  'gclid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
];

function read(): StoredAttribution | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredAttribution) : null;
  } catch {
    return null;
  }
}

// Atribuição válida = capturada dentro da janela de 90 dias (first-touch).
function isValid(attr: StoredAttribution | null): boolean {
  if (!attr?.captured_at) return false;
  const ts = Date.parse(attr.captured_at);
  return !Number.isNaN(ts) && Date.now() - ts < WINDOW_MS;
}

function fromUrl(): Attribution {
  const params = new URLSearchParams(window.location.search);
  const attr: Attribution = {};
  for (const key of PARAMS) {
    const val = params.get(key);
    if (val) attr[key] = val.slice(0, MAX_LEN);
  }
  return attr;
}

/**
 * Roda uma vez no primeiro load (chamado em main.tsx). Se a URL traz gclid/UTMs
 * e ainda não há atribuição válida salva, grava com timestamp. First-touch:
 * nunca sobrescreve uma atribuição válida dentro de 90 dias.
 */
export function captureAttribution(): void {
  try {
    const incoming = fromUrl();
    if (!PARAMS.some((k) => incoming[k])) return; // nada de atribuição na URL

    if (isValid(read())) return; // já existe um first-touch válido — preserva

    const toStore: StoredAttribution = { ...incoming, captured_at: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
  } catch {
    /* localStorage indisponível (modo privado etc.) */
  }
}

/**
 * Retorna apenas os campos de atribuição salvos (sem metadados), prontos para
 * espalhar no signUp (options.data) ou no UPDATE de perfil do fluxo OAuth.
 * Vazio se não houver atribuição ou se estiver expirada.
 */
export function getAttribution(): Attribution {
  const attr = read();
  if (!isValid(attr)) return {};
  const out: Attribution = {};
  for (const k of PARAMS) {
    if (attr![k]) out[k] = attr![k];
  }
  return out;
}
